import React from 'react'
import { useSize } from 'react-measure'
import { useMediaQuery } from 'react-responsive'
import { range } from 'lodash'
import { useHistory } from '~/history'
import { MenuPage, MenuPageItem } from '~/models'
import { memo } from '~/ui/component'
import { HBox, Scroller, Tile, VBox } from '~/ui/components'
import { layout, useStyling } from '~/ui/styling'
import { screenMinWidths } from '~/ui/styling/layout'
import MenuItemSignup from './MenuItemSignup'

export interface Props {
  page: MenuPage

  HeaderComponent?: React.ComponentType<any> | null
  EmptyComponent?:  React.ComponentType<any> | null
}

const MenuGridView = memo('MenuGridView', (props: Props) => {

  const {
    page,
    HeaderComponent,
    EmptyComponent,
  } = props

  const isMobile  = !useMediaQuery({minWidth: screenMinWidths.tablet})

  const gridRef = React.useRef<HTMLDivElement>(null)
  const [gridSize, setGridSize] = React.useState<Size>({width: 0, height: 0})
  useSize(gridRef, {debounce: 100}, setGridSize)

  const availableWidth = React.useMemo(
    () => gridSize.width - itemGap,
    [gridSize.width],
  )

  const {items = []} = page

  const columns = React.useMemo(() => {
    if (gridSize.width === 0 || isMobile) { return 2 }
    return Math.max(1, Math.floor(availableWidth / (minTileWidth + itemGap)))
  }, [availableWidth, gridSize, isMobile])

  const rows = React.useMemo(
    () => Math.ceil(items.length / columns),
    [items, columns],
  )

  const columnWidth = React.useMemo(
    () => Math.round(availableWidth / columns - itemGap),
    [availableWidth, columns],
  )
  const minTileHeight = React.useMemo(
    () => Math.round(columnWidth * 1.1),
    [columnWidth],
  )

  //------
  // Rendering

  function render() {
    return (
      <Scroller ref={gridRef}>
        {items.length === 0 ? (
          EmptyComponent && <EmptyComponent/>
        ) : (
          renderGrid()
        )}
      </Scroller>
    )
  }

  function renderGrid() {
    return (
      <VBox gap={itemGap}>
        {HeaderComponent && <HeaderComponent />}
        <VBox ref={gridRef} gap={itemGap} padding={gridPadding}>
          {range(0, rows).map(renderRow)}
        </VBox>
      </VBox>
    )
  }

  function renderRow(row: number) {
    return (
      <HBox align='stretch' justify={isMobile ? 'left' : 'center'} key={row} gap={itemGap}>
        {items.slice(columns * row, columns * (row + 1)).map(item => (
          <VBox key={item.link.href} flex style={{maxWidth: columnWidth}}>
            <MenuGridTile
              item={item}
              minHeight={minTileHeight}
            />
          </VBox>
        ))}
      </HBox>
    )
  }

  return render()

})

export default MenuGridView

interface MenuGridTileProps {
  item:      MenuPageItem
  minHeight: number
}

const MenuGridTile = memo('MenuGridTile', (props: MenuGridTileProps) => {

  const {item, minHeight} = props
  const caption = item.caption

  const {guide} = useStyling()

  const {location} = useHistory()

  const href = React.useMemo(() => {
    const match = item.link.href.match(/^\/\/page\/(.*)/)
    if (match == null || location.pathname === '/-/') {
      return item.link.href
    } else {
      return `${location.pathname}/${match[1]}`
    }
  }, [item.link.href, location.pathname])

  //------
  // Rendering

  function render() {
    return (
      <Tile
        image={item.image}
        label={caption}
        accessory={renderExtra()}
        href={href}
        target={item.link.target}
        align='center'
        minHeight={minHeight}
        branding={guide.menu.tile}
      />
    )
  }

  function renderExtra() {
    return (
      <>
        {item.extra.signup && <MenuItemSignup item={item} layout='grid'/>}
      </>
    )
  }

  return render()

})

export const gridPadding  = layout.padding.inline.l
export const itemGap      = layout.padding.inline.l
export const minTileWidth = 170
